import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL, categories } from "@/mocks/categories";
import { supportedLangs, articlesByLang } from "@/lib/articles";

const langLabels: Record<string, string> = {
  en: "EN",
  pl: "PL",
  es: "ES",
  fr: "FR",
};

export default function Header({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || mobileOpen;

  const switchLang = (next: string) => {
    const segments = location.pathname.split("/").filter(Boolean);
    const currentSlug = segments.length >= 2 ? segments[segments.length - 1] : "";
    const list = articlesByLang[lang] ?? [];
    const idx = list.findIndex((a) => a.slug === currentSlug);

    if (segments.length >= 2 && idx >= 0) {
      const nextSlug = articlesByLang[next]?.[idx]?.slug ?? currentSlug;
      navigate(`/${next}/${nextSlug}`);
    } else {
      navigate(`/${next}`);
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="mx-auto max-w-7xl h-16 md:h-[72px] flex items-center justify-between">
          <Link
            to={`/${lang}`}
            className={`flex items-center gap-2 font-heading font-bold text-lg whitespace-nowrap transition-colors ${
              solid ? "text-foreground-900" : "text-background-50"
            }`}
            onClick={() => setMobileOpen(false)}
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-extrabold">
              U
            </span>
            <span>usfans spreadsheet</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold">
            <Link
              to={`/${lang}`}
              className={`whitespace-nowrap transition-colors ${
                solid ? "text-foreground-800 hover:text-primary-600" : "text-background-50 hover:text-background-100"
              }`}
            >
              {t("nav.home")}
            </Link>
            <a
              href="#categories"
              className={`whitespace-nowrap transition-colors ${
                solid ? "text-foreground-800 hover:text-primary-600" : "text-background-50 hover:text-background-100"
              }`}
            >
              {t("nav.categories")}
            </a>
            <a
              href="#articles"
              className={`whitespace-nowrap transition-colors ${
                solid ? "text-foreground-800 hover:text-primary-600" : "text-background-50 hover:text-background-100"
              }`}
            >
              {t("nav.shoppingGuide")}
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1 rounded-full bg-background-50/20 px-1 py-1">
              {supportedLangs.map((code) => (
                <button
                  key={code}
                  onClick={() => switchLang(code)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    lang === code
                      ? "bg-primary-500 text-background-50"
                      : solid
                        ? "text-foreground-600 hover:text-foreground-900"
                        : "text-background-50 hover:text-background-100"
                  }`}
                >
                  {langLabels[code]}
                </button>
              ))}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors"
            >
              {t("hero.ctaSecondary")}
            </a>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
                solid ? "text-foreground-800" : "text-background-50"
              }`}
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-3">
            <Link to={`/${lang}`} onClick={() => setMobileOpen(false)} className="text-foreground-900 font-semibold">
              {t("nav.home")}
            </Link>
            <a href="#categories" onClick={() => setMobileOpen(false)} className="text-foreground-900 font-semibold">
              {t("nav.categories")}
            </a>
            <a href="#articles" onClick={() => setMobileOpen(false)} className="text-foreground-900 font-semibold">
              {t("nav.shoppingGuide")}
            </a>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
              {t("categories.title")}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <span className="w-7 h-7 shrink-0 rounded-md overflow-hidden">
                    <img src={cat.image} alt={t(cat.nameKey)} className="w-full h-full object-cover object-top" />
                  </span>
                  <span className="truncate">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1 w-fit">
            {supportedLangs.map((code) => (
              <button
                key={code}
                onClick={() => switchLang(code)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer ${
                  lang === code ? "bg-primary-500 text-background-50" : "text-foreground-600"
                }`}
              >
                {langLabels[code]}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}